import { Link } from 'react-router';
import { cx } from '../lib/cx';
import { Avatar } from './Avatar';
import { Badge, type BadgeTone } from './Badge';

export interface ClientFlag {
  label: string;
  tone: BadgeTone;
}

export interface ClientRowProps {
  name: string;
  to: string;
  subtitle: string;
  avatarUrl?: string | null;
  flags?: readonly ClientFlag[];
  consistencyLabel?: string;
  /** Što brojka desno znači, sitno pod njom — npr. „treninga ovaj tjedan". */
  consistencyCaption?: string;
  className?: string;
}

/**
 * Koliko značaka stane prije nego red postane popis.
 *
 * Klijent s najviše problema imao ih je šest, u dva reda — a šest jednako
 * naglašenih razloga se ne čita, nego preskače. `weeklyReasons` ih slaže po
 * hitnosti, pa prve tri nose ono zbog čega se trener javlja danas; ostatak se
 * broji, da ništa ne izgleda sakriveno.
 */
const MAX_ZNACAKA = 3;

export function ClientRow({
  name,
  to,
  subtitle,
  avatarUrl,
  flags = [],
  consistencyLabel,
  consistencyCaption,
  className,
}: ClientRowProps) {
  const vidljive = flags.slice(0, MAX_ZNACAKA);
  const skriveno = flags.length - vidljive.length;
  return (
    <Link
      to={to}
      className={cx(
        'flex items-center gap-4 rounded-xl border border-line-subtle bg-surface p-4',
        'shadow-card transition-shadow duration-200 hover:shadow-card-raised',
        className,
      )}
    >
      <Avatar name={name} src={avatarUrl ?? null} />
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[15px] text-ink">{name}</span>
        <span className="block truncate text-[12px] text-ink-muted">{subtitle}</span>
        {flags.length > 0 && (
          <span className="mt-2 flex flex-wrap items-center gap-1.5">
            {vidljive.map((f) => (
              <Badge key={f.label} tone={f.tone}>
                {f.label}
              </Badge>
            ))}
            {skriveno > 0 && (
              /* Ostali razlozi se vide u kartonu klijenta; ovdje samo broj. */
              <span className="text-[12px] text-ink-muted">{`+${skriveno}`}</span>
            )}
          </span>
        )}
      </span>
      {consistencyLabel && (
        <span className="shrink-0 text-right">
          <span className="block font-display text-[18px] font-semibold text-ink">
            {consistencyLabel}
          </span>
          {/* Bez natpisa je „2/5" brojka koju treba pogoditi. */}
          {consistencyCaption && (
            <span className="block text-[11px] text-ink-muted">{consistencyCaption}</span>
          )}
        </span>
      )}
    </Link>
  );
}
